import { apiBlob, apiJson } from "./apiClient";

export type ImpactReportState = "available" | "incomplete" | "unsupported";
export type ImpactReportLevel = "bronze" | "silver";
export type ImpactReportLabTypeFilter = "wet" | "dry" | "all";

export interface ImpactMetrics {
  cost_aud: number;
  energy_kwh: number;
  emissions_kgco2e: number;
  waste_kg: number;
  /** Solvent waste, from Green Chemistry only, so Silver wet labs only. */
  solvent_volume_l: number;
  data_tb: number | null;
}

export interface ImpactAssessmentBreakdown {
  assessment_type: string;
  state: ImpactReportState;
  baseline_submission_id: number | null;
  savings_submission_id: number | null;
  metrics: ImpactMetrics;
  missing_reason: string | null;
}

export interface LabImpactReportRow {
  lab_id: string;
  lab_name: string;
  lab_type: "wet" | "dry";
  champion: string | null;
  certification_id: number | null;
  certification_level: string | null;
  certification_state: string | null;
  completed_at: string | null;
  reporting_year: number | null;
  report_state: ImpactReportState;
  metrics: ImpactMetrics;
  assessments: ImpactAssessmentBreakdown[];
  missing_reasons: string[];
}

export interface OrganisationImpactReport {
  organisation_id: string;
  organisation_name: string;
  level: string;
  reporting_year: number;
  lab_type: string | null;
  rows: LabImpactReportRow[];
  totals: ImpactMetrics;
}

export interface ImpactReportYearsResponse {
  organisation_id: string;
  years: number[];
  default_year: number;
}

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Defer revocation so the download has started; revoking synchronously can
  // race the click and produce empty downloads in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

const qs = (params: Record<string, string | undefined>) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (typeof value === "string" && value.length > 0) {
      query.set(key, value);
    }
  });

  const built = query.toString();
  return built ? `?${built}` : "";
};

export class ImpactReportService {
  static async getLabImpactReport(
    labId: string,
    level: ImpactReportLevel = "bronze",
    reporting_year?: number,
  ): Promise<LabImpactReportRow> {
    return apiJson<LabImpactReportRow>(
      `/labs/${labId}/impact-report${qs({
        level,
        reporting_year:
          typeof reporting_year === "number" ? String(reporting_year) : undefined,
      })}`,
    );
  }

  static async downloadLabImpactReportCsv(
    labId: string,
    options?: {
      level?: ImpactReportLevel;
      reporting_year?: number;
      filename?: string;
    },
  ): Promise<void> {
    const level = options?.level ?? "bronze";
    const reportingYear = options?.reporting_year;
    const blob = await apiBlob(
      `/labs/${labId}/impact-report.csv${qs({
        level,
        reporting_year:
          typeof reportingYear === "number" ? String(reportingYear) : undefined,
      })}`,
    );
    const filename =
      options?.filename ??
      (typeof reportingYear === "number"
        ? `impact-report-${labId}-${level}-${reportingYear}.csv`
        : `impact-report-${labId}-${level}.csv`);
    downloadBlob(blob, filename);
  }

  static async getAdminImpactReports(options?: {
    level?: ImpactReportLevel;
    lab_type?: ImpactReportLabTypeFilter;
    reporting_year?: number;
  }): Promise<OrganisationImpactReport> {
    const level = options?.level ?? "bronze";
    const labType = options?.lab_type ?? "wet";
    const reportingYear = options?.reporting_year;
    return apiJson<OrganisationImpactReport>(
      `/admin/impact-reports${qs({
        level,
        lab_type: labType,
        reporting_year:
          typeof reportingYear === "number" ? String(reportingYear) : undefined,
      })}`,
    );
  }

  static async downloadAdminImpactReportCsv(options?: {
    level?: ImpactReportLevel;
    lab_type?: ImpactReportLabTypeFilter;
    reporting_year?: number;
    filename?: string;
  }): Promise<void> {
    const level = options?.level ?? "bronze";
    const labType = options?.lab_type ?? "wet";
    const reportingYear = options?.reporting_year;
    const blob = await apiBlob(
      `/admin/impact-reports.csv${qs({
        level,
        lab_type: labType,
        reporting_year:
          typeof reportingYear === "number" ? String(reportingYear) : undefined,
      })}`,
    );
    const filename =
      options?.filename ??
      (typeof reportingYear === "number"
        ? `impact-reports-${level}-${labType}-${reportingYear}.csv`
        : `impact-reports-${level}-${labType}.csv`);
    downloadBlob(blob, filename);
  }

  static async getAdminImpactReportYears(): Promise<ImpactReportYearsResponse> {
    return apiJson<ImpactReportYearsResponse>("/admin/impact-reports/reporting-years");
  }
}
